import React, { useMemo, useCallback } from 'react'
import classNames from 'classnames'
import styles from './Accounts.module.css'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../../redux/store'
import {
  Cargo,
  setCargosGroup,
  setVisibleSideModal,
  setVisibleContentDeliveries,
  // setCargosGroup
} from '../../../redux/reducers/cargos'
import { cargosSelector } from '../../../redux/selectors/cargos-selector'
import { clientsSelector } from '../../../redux/selectors/clients-selector'
import { getAmount, getItemCurrency } from '../../../utils/getAmount'
import { getRole } from '../../../utils/getRole'

interface ClientWithCargos {
  id: string | number
  name: string
  cargos?: Cargo[]
  entity_name?: string
  currency?: string
}

function ContentDeliveries() {
  const dispatch = useDispatch<AppDispatch>()
  const { clients } = useSelector(clientsSelector)
  const { cargos } = useSelector(cargosSelector)

  const { isClient } = getRole()

  // Мемоизируем проверку мобильного устройства
  const isMobile = useMemo(() => window.innerWidth < 768, [])

  const handleClick = useCallback(
    (clientId: string | number, clientCargos: Cargo[]) => {
      if (isMobile) {
        // На мобиле: показываем второй слайд с грузами
        dispatch(setCargosGroup(clientCargos))
        dispatch(setVisibleContentDeliveries(true))
      } else {
        // На десктопе: открываем боковую панель и сразу показываем второй слайд
        dispatch(setVisibleSideModal(true))
        dispatch(setCargosGroup(clientCargos))
        dispatch(setVisibleContentDeliveries(true))
      }
    },
    [dispatch, isMobile]
  )

  // Мемоизируем отсортированных клиентов с грузами или грузы для клиента
  const sortedClients = useMemo((): ClientWithCargos[] => {
    if (isClient) {
      // Для клиента: получаем все грузы напрямую из массива cargos
      const allCargos = Object.values(cargos || {}).flat()
      return allCargos.length > 0
        ? [{ id: 'client', name: 'Мои грузы', cargos: allCargos }]
        : []
    } else {
      // Для обычных пользователей: показываем ВСЕ группы из cargos (по ключам API),
      // а не только тех клиентов, кто есть в списке clients. Так отображаются и грузы
      // с ключом "" или с клиентом, которого нет в clients (например старые моковые).
      const entries = Object.entries(cargos || {}).filter(
        ([_, list]) => list && list.length > 0
      ) as [string, Cargo[]][]
      return entries
        .map(([clientKey, clientCargos]) => {
          const client =
            clientKey === ''
              ? null
              : clients.find((c) => String(c.id) === clientKey)
          const name = client
            ? client.entity_name || client.name || ''
            : clientKey === ''
              ? 'Прочее'
              : `Клиент №${clientKey}`
          return {
            id: clientKey === '' ? 'other' : clientKey,
            name,
            entity_name: client?.entity_name,
            currency: client?.currency ?? clientCargos[0]?.currency,
            cargos: clientCargos,
          }
        })
        .sort((a, b) => {
          const nameA = (a.name || '').toLowerCase()
          const nameB = (b.name || '').toLowerCase()
          return nameA.localeCompare(nameB, 'ru')
        })
    }
  }, [clients, cargos, isClient])

  return (
    <div>
      {sortedClients.map((client) => {
        const clientCargos = client.cargos || []
        const clientName = client.name || (isClient ? 'Мои грузы' : '')
        const clientCurrency =
          client.currency || clientCargos[0]?.currency || 'BYN'

        if (clientCargos.length === 0) {
          return null
        }

        const totalAmount = clientCargos.reduce(
          (sum, cargo) => sum + cargo.amount,
          0
        )

        return (
          <div
            key={client.id}
            onClick={() => handleClick(client.id, clientCargos)}
            className={classNames(
              styles.shipmentItem,
              styles.bgWhite,
              styles.border10,
              styles.bxShadow,
              styles.small,
              'pt-1 pb-1 mb-2'
            )}
          >
            <div className="row align-items-center ml-2 mr-2">
              <div className={classNames(styles.small2, 'col pl-0 pr-3 mb-1')}>
                <div className={styles.ellipsis2}>
                  <b>{clientName}</b>
                </div>
                <span
                  className={classNames(
                    styles.colorGray,
                    styles.small3,
                    styles.nowrap
                  )}
                >
                  {clientCargos.length} гр.
                </span>
              </div>
              <div
                className={classNames(
                  styles.shipmentCost,
                  'col-auto pl-1 pr-1'
                )}
                style={{ textAlign: 'right' }}
              >
                <b
                  className={classNames(
                    styles.colorRed,
                    styles.nowrap,
                    styles.small2
                  )}
                >
                  {getAmount(totalAmount)} {getItemCurrency(clientCurrency)}
                </b>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// Мемоизируем компонент для предотвращения лишних перерендеров
export default React.memo(ContentDeliveries)
